"use client";

import React, { useCallback, useRef } from "react";
import dynamic from "next/dynamic";
import { useIsPc } from "../../hooks/useIsPc";
import { DrawerH5 } from "../DrawerH5";
import type { PopoverRef } from "../Popover/types";
import { PortfolioContent } from "./PortfolioContent";
import { usePortfolioUI } from "./usePortfolioLogic";
import type { PortfolioContainerProps } from "./types";

const Popover = dynamic(
  () => import("../Popover/Popover").then((mod) => mod.Popover),
  {
    ssr: false,
  },
);

export const PortfolioContainer: React.FC<PortfolioContainerProps> = ({
  children,
  className,
  contentClassName,
  popoverPlacement = "bottom-start",
  mode,
  onItemClick,
  PortfolioListTitle,
  onPopoverStateChange,
  contentId,
  zIndex = 10,
  disabled,
  popoverTrigger = "click",
}) => {
  const [isPc] = useIsPc();
  const popoverRef = useRef<PopoverRef>(null);

  // UI 交互 Hook
  const { drawerOpen, handleDrawerOpen, handleDrawerClose, handleItemClick } =
    usePortfolioUI(mode, onItemClick, isPc, popoverRef);

  const handlePopoverVisibilityChange = useCallback(
    (isVisible: boolean) => {
      onPopoverStateChange?.(isVisible);
    },
    [onPopoverStateChange],
  );

  // Portfolio 内容
  const portfolioContent = (
    <PortfolioContent
      className={contentClassName}
      mode={mode}
      onItemClick={handleItemClick}
      PortfolioListTitle={PortfolioListTitle}
      contentId={contentId}
    />
  );

  // PC 端使用 Popover
  if (isPc) {
    return (
      <Popover
        ref={popoverRef}
        content={portfolioContent}
        trigger={popoverTrigger}
        placement={popoverPlacement}
        className={className}
        zIndex={zIndex}
        disabled={disabled}
        onVisibilityChange={handlePopoverVisibilityChange}
        closeOnFloatingLeave
      >
        {children}
      </Popover>
    );
  }

  // 移动端使用 Drawer
  return (
    <>
      <div
        data-openreplay-label={`PortfolioContainer · PortfolioContainer · div | ${children}`}
        onClick={handleDrawerOpen}
        className={className}
      >
        {children}
      </div>
      <DrawerH5 open={drawerOpen} onClose={handleDrawerClose}>
        {portfolioContent}
      </DrawerH5>
    </>
  );
};
